import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import {
  Grid, GridItem, Button, Flex, FlexItem, ButtonVariant,
  Backdrop, Bullseye, Spinner
} from '@patternfly/react-core';
import { useParams, useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import useApiCall from '../../../services/useApiCall';
import useAPI from '../../../services/useAPI';
import _ from 'lodash';

import { useTranslation } from '../../../localization/useTranslation';
import ModelType from '../../Model/ModelType';
import ModelsContainer from '../../Model/ModelsContainer';
import { getTelemetry } from '../../../utils/dtdl';
import { hasValidInput } from '../../Model/ModelUtils';
import Icons, { Size } from '../../Common/Icons';
import { format } from '../../../utils/string';
import DeleteAlarm from './DeleteAlarm';
import ContentPage from '../../ContentView/ContentPage/ContentPage';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import AlertContext from 'services/AlertContext';
import { useOrgIdSelect } from 'components/Select/useOrgIdSelect';
import { privileges } from 'utils/types';

const boundedSeverity = (value) => {
  const min = 1;
  const max = 5;
  if (value < min) {
    return min;
  } else if (value > max) {
    return max;
  }
  return value;

};

const ALARM_DEF = Object.freeze({
  id: '',
  name: '',
  type: '',
  description: '',
  entityTypeId: '',
  entityIds: [],
  severity: 1,
  condition: ''
});


const operators = [
  { text: '==', value: '==' },
  { text: '<', value: '<' },
  { text: '<=', value: '<=' },
  { text: '>', value: '>' },
  { text: '>=', value: '>=' },
  { text: '!=', value: '!=' }
];


const deserializeCondition = (serializedCondition) => {
  if (!serializedCondition) {
    return;
  }

  const rawCondition = JSON.parse(serializedCondition);
  const keys = Object.keys(rawCondition);
  return {
    variable: rawCondition[keys[0]][0].var,
    operator: keys[0],
    value: rawCondition[keys[0]][1]
  };
};

const serializeCondition = (newCondition) => {
  if (!newCondition) {
    return;
  }

  return JSON.stringify({
    [newCondition.operator]: [
      { var: newCondition.variable.name },
      newCondition.value
    ]
  });
};

export const createAlarmDefinitionQuery = (api, alarmId, options) => ({
  queryKey: ['alarmDefinition', alarmId],
  queryFn: createQueryRequest(api.core.getAlarm),
  ...options
});

export const useAlarmDefinitionQuery = (alarmId, options) => {
  const api = useAPI();
  return useQuery(createAlarmDefinitionQuery(api, alarmId, options));
};

export const AlarmName = ({ alarmId }) => {
  const alarmDefinitionQuery = useAlarmDefinitionQuery(alarmId);
  return alarmDefinitionQuery.isLoading ? <BreadcrumbSkeleton /> : (alarmDefinitionQuery.data?.name ?? '');
};

const AlarmView = ({ breadcrumbs }) => {
  const api = useAPI();
  const T = useTranslation();
  const { addErrorAlert } = useContext(AlertContext);

  const { alarmId } = useParams();
  const history = useHistory();
  const [assets, setAssets] = useState([]);
  const [assetModels, setAssetModels] = useState([]);
  const [alarmEntity, setAlarmEntity] = useState();
  const [alarmDefinition, setAlarmDefinition] = useState({ ...ALARM_DEF, limitToAssets: false, rule: { value: 1 }, assets: [] });
  const [telemetry, setTelemetry] = useState();
  const [currentAssetModel, setCurrentAssetModel] = useState({});
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const { renderOrgIdSelect, currentOrg } = useOrgIdSelect({ onChange: null, neededPrivilege: privileges.AlarmManagement });

  const [createAlarmDefinition, isCreatingAlarmDefinition] = useApiCall({
    apiCall: api.core.createAlarm,
    onSuccess: useCallback(() => {
      history.push('/admin/alarms');
    }, [history]),
    onError: useCallback((error) => {
      addErrorAlert(format(T.admin.alarm.saveFailed, error));
    }, [T.admin.alarm.saveFailed, addErrorAlert])
  });

  const [updateAlarmDefinition, isUpdatingAlarmDefinition] = useApiCall({
    apiCall: api.core.updateAlarm,
    onSuccess: useCallback(() => {
      history.push('/admin/alarms');
    }, [history]),
    onError: useCallback((error) => {
      addErrorAlert(format(T.admin.alarm.saveFailed, error));
    }, [T.admin.alarm.saveFailed, addErrorAlert])
  });


  // TODO: use loading state
  const [getModels, isLoadingModels] = useApiCall({
    apiCall: api.core.getModels,
    onSuccess: useCallback((models) => {
      setAssetModels(models);
    }, []),
    onError: useCallback(() => {

    }, [])
  });

  // TODO: use loading state
  const [getModel] = useApiCall({
    apiCall: api.core.getModel,
    onSuccess: useCallback((model) => {
      const mainModel = model.modelData[0];
      const tel = getTelemetry({ dtdlModel: mainModel, component: '.' });
      setTelemetry(_.map(tel, t => ({ name: t.name, id: t.name, displayName: t.displayName })));
    }, []),
    onError: useCallback(() => {

    }, [])
  });

  // TODO: use loading state
  const [getAssetsByModel, isLoadingAssets] = useApiCall({
    apiCall: api.core.getAssetsByModel,
    onSuccess: useCallback((assets) => {
      setAssets(assets);
    }, []),
    onError: useCallback(() => {

    }, [])
  });
  // const assetsByModelQuery = useQuery({
  //   queryKey: ['assetsByModel', currentAssetModel?.id],
  //   queryFn: createQueryRequest(api.core.getAssetsByModel),
  //   enabled: !_.isEmpty(currentAssetModel),
  //   onSuccess: useCallback((assets) => {
  //     setAssets(assets);
  //   }, []),


  const [getAlarmDefinitionFull, isLoading] = useApiCall({
    apiCall: api.core.getAlarmDefinitionFull,
    onSuccess: useCallback(({ alarmEntity, assetModels, assetModel, assets }) => {
      const mainModel = assetModel.modelData[0];
      const tel = getTelemetry({ dtdlModel: mainModel, component: '.' });
      const telemetry = _.map(tel, t => ({ name: t.name, id: t.name, displayName: t.displayName }));
      const rule = deserializeCondition(alarmEntity.condition);
      const v = _.filter(telemetry, t => t.name === rule.variable);
      const a = _.filter(assets, asset => {
        return _.some(alarmEntity.entityIds, id => id === asset.id);
      });
      const assetModelId = _.filter(assetModels, model => model.id === alarmEntity.entityTypeId)[0];
      setAlarmDefinition({
        ...alarmEntity,
        assetModelId,
        rule: { ...deserializeCondition(alarmEntity.condition), variable: v[0] },
        limitToAssets: !_.isEmpty(alarmEntity.entityIds),
        assets: a
      });
      setTelemetry(telemetry);
      setAssetModels(assetModels);
      setAssets(assets);
      setAlarmEntity(alarmEntity);
      setCurrentAssetModel(assetModelId);
    }, []),
    onError: useCallback((error) => {
      console.error(error);
    }, [])
  });


  useEffect(() => {
    if (alarmId) {
      getAlarmDefinitionFull(alarmId);
    } else {
      getModels();
    }
  }, [alarmId, getAlarmDefinitionFull, getModels]);

  const saveAlarm = useCallback(() => {
    const a = {
      ...alarmDefinition,
      entityTypeId: (_.isEmpty(alarmDefinition.assets)) ? alarmDefinition.assetModelId.id : '',
      entityIds: _.map(alarmDefinition.assets, asset => asset.id),
      condition: serializeCondition(alarmDefinition.rule)
    };
    const newAlarm = _.pick(a, _.keys(ALARM_DEF));
    if (alarmEntity) {
      newAlarm.id = alarmEntity.id;
      updateAlarmDefinition(newAlarm);
    } else {
      newAlarm.id = uuidv4();
      newAlarm.organizationId = currentOrg.id;
      createAlarmDefinition(newAlarm);
    }

  }, [alarmDefinition, alarmEntity, createAlarmDefinition, currentOrg?.id, updateAlarmDefinition]);

  const model = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        label: T.common.name,
        path: 'name',
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.NUMBER_INPUT,
        label: 'Severity',
        path: 'severity',
        isRequired: true,
        properties: {
          onPlus: () => setAlarmDefinition({ ...alarmDefinition, severity: boundedSeverity(alarmDefinition.severity + 1) }),
          onMinus: () => setAlarmDefinition({ ...alarmDefinition, severity: boundedSeverity(alarmDefinition.severity - 1) })
        }
      },
      {
        type: ModelType.TEXTAREA,
        label: T.common.description,
        path: 'description',
        isRequired: false
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.common.model,
        path: 'assetModelId',
        options: _.map(assetModels, (value) => ({ value, text: `${value.displayName} (${value.id})`, description: value.id })),
        disabled: (alarmId) ? true : false,
        isRequired: true,
        hasError: _.isEmpty,
        properties: {
          isTypeahead: true,
          isLoading: isLoadingModels
        }
      },
      {
        type: ModelType.CHECKBOX,
        label: T.admin.alarms.limitToAssets,
        path: 'limitToAssets',
        isRequired: false
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.common.assets,
        path: 'assets',
        isRequired: false,
        disabled: !alarmDefinition?.limitToAssets ?? true,
        options: _.map(assets, (value) => ({ value, text: value.displayName })),
        properties: {
          isClearable: true,
          isMultiple: true,
          isTypeahead: true,
          isLoading: isLoadingAssets
        }
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.admin.alarms.rule.variable,
        path: 'rule.variable',
        options: _.map(telemetry, value => ({ value, text: value.displayName })),
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.admin.alarms.rule.operator,
        path: 'rule.operator',
        options: operators,
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.NUMBER_INPUT,
        label: T.admin.alarms.rule.value,
        path: 'rule.value',
        isRequired: true,
        properties: {
          widthChars: 13,
          onPlus: () => setAlarmDefinition({ ...alarmDefinition, rule: { ...alarmDefinition.rule, value: parseFloat(alarmDefinition.rule.value) + 1 } }),
          onMinus: () => setAlarmDefinition({ ...alarmDefinition, rule: { ...alarmDefinition.rule, value: parseFloat(alarmDefinition.rule.value) - 1 } })
        }
        // hasError: _.isEmpty
      }
    ];
  }, [T.admin.alarms.limitToAssets, T.admin.alarms.rule.operator, T.admin.alarms.rule.value, T.admin.alarms.rule.variable, T.common.assets, T.common.description, T.common.model, T.common.name, alarmDefinition, alarmId, assetModels, assets, isLoadingAssets, isLoadingModels, telemetry]);


  const onChangeData = useCallback((data) => {
    if (!_.isEqual(data.assetModelId, currentAssetModel)) {
      if (!_.isEmpty(data.assetModelId)) {
        getModel(data.assetModelId.id);
        setTelemetry([]);
        getAssetsByModel(data.assetModelId.id);
        setCurrentAssetModel(data.assetModelId);
      }
    }
    setAlarmDefinition(data);
  }, [currentAssetModel, getAssetsByModel, getModel]);

  const backdropSpinner = useCallback(() => {
    return (
      <Backdrop>
        <Bullseye>
          <Spinner aria-label="Creating new Version" aria-valuetext="Creating..." />
        </Bullseye>
      </Backdrop>
    );
  }, []);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/alarms/view'}>
      <Grid hasGutter >
        <GridItem span={5} rowSpan={6} >
          <ModelsContainer models={model} data={alarmDefinition} setData={onChangeData}>
            {
              (renderModel) => {
                return (
                  <div>
                    <div>{ renderModel('name') }</div>
                    <div>{ renderModel('description') }</div>
                    <div>{ renderModel('severity') }</div>
                    <div>{ renderModel('assetModelId') }</div>
                    <div>{ renderModel('limitToAssets') }</div>
                    <div>{ renderModel('assets') }</div>
                  </div>
                );
              }
            }
          </ModelsContainer>
        </GridItem>
        <GridItem span={7} rowSpan={6} />
        <GridItem span={2} rowSpan={1} >
          <ModelsContainer models={model} data={alarmDefinition} setData={onChangeData}>
            {
              (renderModel) => {
                return (
                  <div>
                    <div>{ renderModel('rule.variable') }</div>
                  </div>
                );
              }
            }
          </ModelsContainer>
        </GridItem>
        <GridItem span={2} rowSpan={1} >
          <ModelsContainer models={model} data={alarmDefinition} setData={onChangeData}>
            {
              (renderModel) => {
                return (
                  <div>
                    <div>{ renderModel('rule.operator') }</div>
                  </div>
                );
              }
            }
          </ModelsContainer>
        </GridItem>
        <GridItem span={2} rowSpan={1} >
          <ModelsContainer models={model} data={alarmDefinition} setData={onChangeData}>
            {
              (renderModel) => {
                return (
                  <div>
                    <div>{ renderModel('rule.value') }</div>
                  </div>
                );
              }
            }
          </ModelsContainer>
        </GridItem>
        <GridItem span={7} rowSpan={1} />
        <GridItem span={12} rowSpan={1} >
          { !alarmId && renderOrgIdSelect() }
        </GridItem>
        <GridItem span={2} rowSpan={1} >
          <Flex>
            <FlexItem>
              <Button
                isLoading={isCreatingAlarmDefinition || isUpdatingAlarmDefinition}
                isDisabled={!hasValidInput(alarmDefinition, model) || _.isEmpty(currentOrg)}
                onClick={saveAlarm}>
                { T.common.save }
              </Button>
            </FlexItem>
            <FlexItem>
              <Button
                isDisabled={_.isEmpty(alarmEntity)}
                icon={<Icons.Delete size={Size.SMALL} />}
                variant={ButtonVariant.danger}
                onClick={() => {
                  setConfirmDeleteItem(alarmEntity);
                }}>
                { T.common.delete }
              </Button>
            </FlexItem>
          </Flex>
        </GridItem>
      </Grid>
      <DeleteAlarm
        confirmDeleteItem={confirmDeleteItem}
        onDelete={() => {
          setConfirmDeleteItem(null);
          history.push('/admin/alarms');
        }}
        onCancel={() => {
          setConfirmDeleteItem(null);
        }}
      />
      { isLoading && backdropSpinner() }
    </ContentPage>
  );
};

export default AlarmView;
